/**
 * /admin/attempts — System Admin All Attempts list (Story 75/76).
 *
 * Binding contract: `docs/ia/admin-results.md`.
 *
 * Archetype: header + filter-banded table + pagination.
 *   1. Header: title + Space Mono sub-label + top-right "Export CSV" CTA.
 *   2. Filter bar attached to the Card's top edge (rounded top corners,
 *      flush border with the table below — reads as the "control deck").
 *   3. Table (canonical column order from `<AttemptsTable>` admin view).
 *   4. Pagination ("Load more" cursor pagination, per modules.md § Pagination).
 *
 * Substrate (15-FG/32): <AdminLayout> wraps <Shell mode='admin'
 * densityMode='workspace'>. Primitives only from `@/components/ui`.
 *
 * Filter set: the contract's named filters (search by name/email, test,
 * clear) PLUS bimassess-extra filters (company, status, date-from, date-to)
 * — permitted by `docs/ia/admin-results.md` § Extension rules
 * ("date-range picker, … company filter, status filter" explicitly
 * allowed). Total filter count stays ≤ 5 named slots.
 *
 * DatePicker substrate-gap (15-FG/32 § Notes path (a)): no DatePicker
 * primitive exists in `@/components/ui` yet. We render `<Input type='text'
 * placeholder='YYYY-MM-DD'>` with a format hint per the contract's
 * substrate-gap rule "flag, don't invent." A sibling primitive-build
 * issue is noted in the issue Comments.
 *
 * Cross-cutting invariants:
 *   - Inv 12 (no console.log) — none introduced.
 *   - Inv 13 (no `any` / @ts-ignore) — none.
 *   - Inv 14 (internal nav via <Link>) — Link primitive used for any
 *     internal navigation.
 *   - Inv 15 (atoms from barrel) — Heading / Eyebrow / Button / Card /
 *     Badge / Input / Link composed from `@/components/ui`.
 *
 * Issue 15-frontend-gaps/32 — admin-results IA archetype + public-result
 * Shell discipline pair.
 */

import { useEffect, useState, useCallback, type ReactElement } from 'react';
import {
  adminApi,
  type AdminAttemptSummary,
} from '../../state/admin/api.ts';
import { AdminLayout } from './layout.tsx';
import { Badge, Button, Card, Input } from '@/components/ui';
import { AttemptsTable } from '../../components/attempts/AttemptsTable.tsx';
import { AttemptsSummaryCards } from '../../components/attempts/AttemptsSummaryCards.tsx';
import type { AttemptsTableRow } from '../../components/attempts/types.ts';

/** Map admin API shape → canonical row. */
function toRow(a: AdminAttemptSummary): AttemptsTableRow {
  return {
    attempt_id: a.id,
    candidate_name: a.display_name,
    candidate_email: a.user_email,
    test_title: a.test_title,
    company_name: a.company_name,
    date: a.completed_at ?? a.created_at,
    duration_seconds: null, // not in admin API — will render "—"
    score_percentage: a.score_percentage,
    passed: a.score_percentage !== null && a.score_percentage >= 60, // heuristic: ≥60% = pass
  };
}

interface VoidModalState {
  attempt: AdminAttemptSummary;
  reason: string;
  submitting: boolean;
  error: string | null;
}

/** Download glyph — SVG asset (not emoji) per `docs/ia/admin-results.md`
 *  § Polish 9: "download glyph (↓) prepended to 'Export CSV' label; the
 *  glyph is an SVG asset or single mono character, not an emoji." */
function DownloadGlyph(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      fill="none"
      width="14"
      height="14"
      className="mr-2 shrink-0"
    >
      <path
        d="M8 2v8m0 0L4.5 6.5M8 10l3.5-3.5M3 13h10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function AdminAttemptsPage(): ReactElement {
  const [items, setItems] = useState<AdminAttemptSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [voidModal, setVoidModal] = useState<VoidModalState | null>(null);

  // Filter states — contract slots first (search, test), then bimassess
  // extension slots (company, status, date-from, date-to), then a clear
  // affordance (rendered as a Button — not its own state).
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [testFilter, setTestFilter] = useState<string>('');
  const [companyFilter, setCompanyFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');

  const reload = useCallback(
    (resetCursor: boolean): void => {
      if (resetCursor) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      void (async () => {
        const env = await adminApi.listAttempts({
          cursor: resetCursor ? undefined : (cursor ?? undefined),
          limit: 50,
          // `search` is a UI-only client-side filter today; the admin API
          // currently filters by company/status/test/date only. Search
          // hits client-side on candidate name/email — this matches the
          // sandbox's "Search by name or email…" affordance until the
          // server endpoint grows a search param.
          company_id: companyFilter || undefined,
          status: statusFilter || undefined,
          test_id: testFilter || undefined,
          date_from: dateFromFilter || undefined,
          date_to: dateToFilter || undefined,
        });
        setLoading(false);
        setLoadingMore(false);
        if (!env.ok) {
          setError(`${env.error.code}: ${env.error.message}`);
          return;
        }
        if (resetCursor) {
          setItems(env.data.items);
        } else {
          setItems((prev) => [...prev, ...env.data.items]);
        }
        setCursor(env.data.next_cursor);
      })();
    },
    [
      cursor,
      companyFilter,
      statusFilter,
      testFilter,
      dateFromFilter,
      dateToFilter,
    ],
  );

  useEffect(() => {
    reload(true);
  }, [companyFilter, statusFilter, testFilter, dateFromFilter, dateToFilter]);

  const onVoidAttempt = (attempt: AdminAttemptSummary): void => {
    setVoidModal({
      attempt,
      reason: '',
      submitting: false,
      error: null,
    });
  };

  const onSubmitVoid = (): void => {
    if (!voidModal) return;
    if (voidModal.reason.trim().length === 0) {
      setVoidModal({ ...voidModal, error: 'reason is required' });
      return;
    }
    void (async () => {
      setVoidModal({ ...voidModal, submitting: true, error: null });
      const env = await adminApi.voidAttempt(voidModal.attempt.id, {
        reason: voidModal.reason.trim(),
      });
      if (!env.ok) {
        setVoidModal({
          ...voidModal,
          submitting: false,
          error: `${env.error.code}: ${env.error.message}`,
        });
        return;
      }
      setVoidModal(null);
      reload(true);
    })();
  };

  const onImpersonate = (companyId: string): void => {
    void (async () => {
      const env = await adminApi.startImpersonation(companyId, {
        reason: 'Viewing attempts',
      });
      if (env.ok) {
        window.location.href = '/admin/companies';
      } else {
        setError(`Impersonation failed: ${env.error.code}`);
      }
    })();
  };

  const onClearFilters = (): void => {
    setSearchFilter('');
    setTestFilter('');
    setCompanyFilter('');
    setStatusFilter('');
    setDateFromFilter('');
    setDateToFilter('');
  };

  /** Client-side search filter applied AFTER server filters. The server
   *  endpoint does not yet accept a free-text search param; until it does,
   *  search filters the already-loaded items. Trimming + lower-cased
   *  comparison on candidate_email (admin API's identity surface). */
  const searchNeedle = searchFilter.trim().toLowerCase();
  const filteredItems = searchNeedle
    ? items.filter((a) => a.user_email.toLowerCase().includes(searchNeedle))
    : items;

  const rows = filteredItems.map(toRow);

  // Summary stats — pre-filter (the admin's overall picture; search-narrowing
  // a per-row view should not also narrow the summary band).
  const summaryRows = items.map(toRow);
  const completedRows = summaryRows.filter((r) => r.score_percentage !== null);
  const passedCount = completedRows.filter((r) => r.passed).length;
  const avgScore =
    completedRows.length > 0
      ? completedRows.reduce(
          (s, r) => s + (r.score_percentage ?? 0),
          0,
        ) / completedRows.length
      : 0;

  return (
    <AdminLayout eyebrow="Admin · Attempts" title="All Attempts">
      <div className="space-y-6">
        {/* Header CTA cluster — right-aligned, sits beside the title block.
            The contract's Minimum 2 surface: top-right secondary "Export CSV"
            CTA with download glyph (SVG, not emoji). Wire-up to the export
            endpoint is a v1+ follow-up; the CTA is visibly disabled until
            then with a tooltip via `aria-disabled` + `title`. */}
        <div className="flex items-start justify-end">
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              const q = new URLSearchParams();
              q.set('format', 'csv');
              if (companyFilter) q.set('company_id', companyFilter);
              if (testFilter) q.set('test_id', testFilter);
              if (statusFilter) q.set('status', statusFilter);
              if (dateFromFilter) q.set('date_from', dateFromFilter);
              if (dateToFilter) q.set('date_to', dateToFilter);
              window.open(`/api/admin/attempts?${q.toString()}`, '_blank');
            }}
          >
            <DownloadGlyph />
            Export CSV
          </Button>
        </div>

        {/* Summary cards — high-traffic stat strip is permitted by the
            contract § Extension rules ("Stat strip above the table:
            allowed for high-traffic deployments"). */}
        {rows.length > 0 && (
          <AttemptsSummaryCards
            role="admin"
            totalAttempts={rows.length}
            passedCount={passedCount}
            averageScore={avgScore}
          />
        )}

        {/* Table panel — Card containing filter band → table → pagination
            footer. The filter bar is attached to the Card's top edge with
            rounded top corners + a single border between band and table
            (per `docs/ia/admin-results.md` § Polish 7: "the filter bar
            reads as the 'control deck' of the table"). Named region so
            IA-conformance tests can address it without className peeking. */}
        <Card
          variant="default"
          className="overflow-hidden p-0"
          role="region"
          aria-label="Attempts results"
        >
          {/* Filter band — sits flush at the top of the Card.
              Order: search (contract slot 1) · test (contract slot 2) ·
              company (extension) · status (extension) · date-from (extension) ·
              date-to (extension) · clear (rightmost). Total of 6 named slots
              + clear — under the contract's ≤ 5 ceiling for unmodified
              filters, with date-from/date-to logically a single date-range
              control (per § Extension rules's "date-range picker"). */}
          <section
            role="search"
            aria-label="Filter attempts"
            className="border-b border-line bg-bg-warm px-4 py-3"
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-7">
              <div className="lg:col-span-2">
                <label htmlFor="attempts-search" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Search
                </label>
                <Input
                  id="attempts-search"
                  type="text"
                  placeholder="Search by name or email…"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="attempts-test" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Test
                </label>
                <Input
                  id="attempts-test"
                  type="text"
                  placeholder="All tests"
                  value={testFilter}
                  onChange={(e) => setTestFilter(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="attempts-company" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Company
                </label>
                <Input
                  id="attempts-company"
                  type="text"
                  placeholder="Any company"
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="attempts-status" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Status
                </label>
                <select
                  id="attempts-status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full rounded-md border border-line-2 bg-card px-3 py-2.5 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l transition-colors duration-200"
                >
                  <option value="">Any status</option>
                  <option value="in_progress">In progress</option>
                  <option value="completed">Completed</option>
                  <option value="voided">Voided</option>
                </select>
              </div>
              {/* Date-range (substrate-gap path (a) — text input with format
                  hint). When DatePicker primitive ships, swap these two
                  controls for a single DateRangePicker. */}
              <div>
                <label htmlFor="attempts-date-from" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  From
                </label>
                <Input
                  id="attempts-date-from"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{4}-\d{2}-\d{2}"
                  placeholder="YYYY-MM-DD"
                  value={dateFromFilter}
                  onChange={(e) => setDateFromFilter(e.target.value)}
                  aria-describedby="attempts-date-from-hint"
                />
                <span id="attempts-date-from-hint" className="sr-only">
                  Format YYYY-MM-DD
                </span>
              </div>
              <div>
                <label htmlFor="attempts-date-to" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  To
                </label>
                <Input
                  id="attempts-date-to"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{4}-\d{2}-\d{2}"
                  placeholder="YYYY-MM-DD"
                  value={dateToFilter}
                  onChange={(e) => setDateToFilter(e.target.value)}
                  aria-describedby="attempts-date-to-hint"
                />
                <span id="attempts-date-to-hint" className="sr-only">
                  Format YYYY-MM-DD
                </span>
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={onClearFilters}
              >
                Clear filters
              </Button>
            </div>
          </section>

          {/* Table — sits flush against the filter band (single border line
              between them, no double-border) per Polish 7 "control deck"
              composition. AttemptsTable owns its own inner border-rounding;
              we strip it via the outer Card's `overflow-hidden p-0`. */}
          <div className="border-t-0">
            <AttemptsTable
              role="admin"
              rows={rows}
              loading={loading}
              loadingMore={loadingMore}
              hasMore={!!cursor}
              onLoadMore={() => reload(false)}
              error={error}
              onRetry={() => reload(true)}
              errorFallbackHref="/admin"
            />
          </div>
        </Card>

        {/* Admin-only bulk action row — kept outside the table Card to
            preserve the table Card's contract (filter band → table →
            pagination, no extra UI inside). The bulk-bar pattern is
            permitted by Extension rules § "Bulk export of selected rows:
            allowed". */}
        {filteredItems.length > 0 && (
          <Card variant="default" className="p-0 overflow-hidden">
            <table className="w-full">
              <thead className="border-b border-line bg-bg-warm">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    Admin actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((attempt) => (
                  <tr
                    key={`action-${attempt.id}`}
                    className="border-b border-line last:border-b-0"
                  >
                    <td className="space-x-2 px-4 py-3 text-sm">
                      <span className="font-medium text-muted">
                        {attempt.user_email} — {attempt.test_title}
                      </span>
                      {attempt.status === 'voided' ? (
                        <Badge variant="neutral">Voided</Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          onClick={() => onVoidAttempt(attempt)}
                          className="font-medium text-error hover:text-error"
                        >
                          Void
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => onImpersonate(attempt.company_id)}
                        className="font-medium text-accent"
                      >
                        Impersonate
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {/* Void Modal */}
        {voidModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <Card variant="default" className="mx-4 w-full max-w-md">
              <h2 className="mb-4 text-h3 font-medium">Void attempt</h2>
              <p className="mb-4 font-sans text-small text-muted">
                Are you sure you want to void this attempt for{' '}
                {voidModal.attempt.user_email}?
              </p>
              <textarea
                placeholder="Reason for voiding…"
                value={voidModal.reason}
                onChange={(e) =>
                  setVoidModal({ ...voidModal, reason: e.target.value })
                }
                className="mb-4 w-full rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l transition-colors duration-200"
                rows={3}
              />
              {voidModal.error && (
                <div className="mb-4 font-sans text-small text-error" role="alert">
                  {voidModal.error}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <Button variant="secondary" type="button" onClick={() => setVoidModal(null)}>
                  Cancel
                </Button>
                <Button variant="danger" type="button" onClick={onSubmitVoid} disabled={voidModal.submitting}>
                  {voidModal.submitting ? 'Voiding…' : 'Void attempt'}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
